import React, { useState, Children } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { Element } from 'react-scroll';
import classNames from 'classnames';
import { Player } from '@lottiefiles/react-lottie-player';

import ContentDivider from '../shared/ContentDivider';
import PlayIcon from '../shared/PlayIcon';
import DesktopAndTabletContainerFull from '../../shared/DesktopAndTabletContainerFullWidth';
import MobileContainerFull from '../../shared/MobileContainerFullWidth';
import DesktopContainer from '../../shared/DesktopContainer';
import TabletAndMobileContainer from '../../shared/TabletAndMobileContainer';
import ConditionalWrapper from '../../shared/conditionalWrapper/ConditionalWrapper';
import VideoModal from '../shared/VideoModal';
import LazyLoadImg from '../shared/LazyLoadImg';

import { animationShape } from '../../../types';

const DetailedContentComponent = ({ detailedContentItems, hasContentDivider }) => {
  const [selectedContentGuid, selectContent] = useState(null);

  const setSelectedContent = guid => {
    if (guid === selectedContentGuid) {
      selectContent(null);
    } else {
      selectContent(guid);
    }
  };

  const selectedContent =
    detailedContentItems && detailedContentItems.find(item => item.guid === selectedContentGuid);

  ReactModal.setAppElement('#wrapper');

  return (
    <section className="section-white">
      <div className="container-fluid">
        {Children.toArray(
          detailedContentItems.map(item => (
            <div className="section-gutter-half">
              <ConditionalWrapper
                condition={item.prettyUrlTitle}
                wrapper={children => <Element name={item.prettyUrlTitle}>{children}</Element>}
              >
                <div className={`section-intro ${item.textPosition}`}>
                  <div className="content">
                    <h2>
                      <span dangerouslySetInnerHTML={{ __html: item.title }} />
                    </h2>
                    <div dangerouslySetInnerHTML={{ __html: item.description }} />
                  </div>

                  <div className={classNames(!item.animation?.animationUrl && 'media')}>
                    <ConditionalWrapper
                      condition={item.videoUrl}
                      wrapper={children => (
                        <button
                          className="btn-no-style"
                          type="button"
                          onClick={() => setSelectedContent(item.guid)}
                        >
                          {children}
                        </button>
                      )}
                    >
                      {item.videoUrl && <PlayIcon iconColor={item.playIconColor} />}

                      {/* IMAGE + CAPTION */}
                      {item.image && item.image.url && (
                        <div className="dc-media">
                          <ConditionalWrapper
                            condition={item.imageUrl && !item.videoUrl}
                            wrapper={children => (
                              <a
                                href={item.imageUrl}
                                target={item.imageUrlTarget}
                                title={`Link for ${item.altText || item.title}`}
                              >
                                {children}
                              </a>
                            )}
                          >
                            {item.imageMobile && item.imageMobile.url ? (
                              <>
                                <MobileContainerFull>
                                  <LazyLoadImg
                                    src={item.imageMobile}
                                    alt={item.altText ? item.altText : `Media for ${item.title}`}
                                    className="dc-image"
                                  />
                                </MobileContainerFull>
                                <DesktopAndTabletContainerFull>
                                  <LazyLoadImg
                                    src={item.image}
                                    alt={item.altText ? item.altText : `Media for ${item.title}`}
                                    className="dc-image"
                                  />
                                </DesktopAndTabletContainerFull>
                              </>
                            ) : (
                              <LazyLoadImg
                                src={item.image}
                                alt={item.altText ? item.altText : `Media for ${item.title}`}
                                className="dc-image"
                              />
                            )}
                          </ConditionalWrapper>

                          {/* Caption renders only when image exists */}
                          {item.caption && (
                            <div
                              className="dc-caption"
                              // Assumes server-side sanitization of allowed inline tags/attrs
                              dangerouslySetInnerHTML={{ __html: item.caption }}
                            />
                          )}
                        </div>
                      )}

                      {/* ANIMATION (no caption when only animation is present) */}
                      {item.animation?.animationUrl && (
                        <>
                          <DesktopContainer>
                            <Player
                              src={item.animation.animationUrl}
                              background={item.animation.backgroundColor}
                              speed={item.animation.animationSpeed}
                              style={{
                                width: item.animation.width,
                                height: item.animation.height
                              }}
                              loop={item.animation.loop}
                              autoplay={item.animation.autoplay}
                              hover={item.animation.hover}
                              mode={item.animation.playMode}
                            />
                          </DesktopContainer>
                          <TabletAndMobileContainer>
                            <Player
                              src={item.animation.animationUrl}
                              background={item.animation.backgroundColor}
                              speed={item.animation.animationSpeed}
                              style={{
                                width: item.animation.deviceWidth,
                                height: item.animation.deviceHeight
                              }}
                              loop={item.animation.loop}
                              autoplay={item.animation.autoplay}
                              hover={item.animation.hover}
                              mode={item.animation.playMode}
                            />
                          </TabletAndMobileContainer>
                        </>
                      )}
                    </ConditionalWrapper>
                  </div>
                </div>
              </ConditionalWrapper>
            </div>
          ))
        )}

        <VideoModal
          {...selectedContent}
          selectFunction={setSelectedContent}
          isActive={!!selectedContentGuid}
        />

        {hasContentDivider && <ContentDivider />}
      </div>
    </section>
  );
};

DetailedContentComponent.defaultProps = {
  detailedContentItems: [],
  hasContentDivider: false,
  animation: {}
};

DetailedContentComponent.propTypes = {
  detailedContentItems: PropTypes.arrayOf(PropTypes.object),
  hasContentDivider: PropTypes.bool,
  animation: animationShape
};

export default DetailedContentComponent;
